import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import Header from "../Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import {
  IcSync, IcCog, IcPuzzle, IcTag,
  IcFileInvoiceDollar, IcHandHoldingUsd, IcChartLine, IcReceipt,
  IcUsers, IcWallet, IcCalendar, IcCalendarCheck, IcCheckCircle, IcPauseCircle,
  IcPlusCircle, IcFileAlt, IcChartBar, IcChartPie, IcTable,
  IcExclamation, IcClipboard, IcFileInvoice,
} from "../../Icons";

moment.locale("es");

// Mapa FA class → componente SVG
const ICON_MAP = {
  "fas fa-sync-alt":             IcSync,
  "fas fa-cog":                  IcCog,
  "fas fa-puzzle-piece":         IcPuzzle,
  "fas fa-tag":                  IcTag,
  "fas fa-file-invoice-dollar":  IcFileInvoiceDollar,
  "fas fa-hand-holding-usd":     IcHandHoldingUsd,
  "fas fa-chart-line":           IcChartLine,
  "fas fa-receipt":              IcReceipt,
  "fas fa-users":                IcUsers,
  "fas fa-wallet":               IcWallet,
  "fas fa-calendar-alt":         IcCalendar,
  "fas fa-calendar-check":       IcCalendarCheck,
  "fas fa-check-circle":         IcCheckCircle,
  "fas fa-pause-circle":         IcPauseCircle,
  "fas fa-plus-circle":          IcPlusCircle,
  "fas fa-file-alt":             IcFileAlt,
  "fas fa-chart-bar":            IcChartBar,
  "fas fa-chart-area":           IcChartLine,
  "fas fa-chart-pie":            IcChartPie,
  "fas fa-table":                IcTable,
  "fas fa-exclamation-circle":   IcExclamation,
  "fas fa-clipboard-list":       IcClipboard,
  "fas fa-file-invoice":         IcFileInvoice,
};

function IconSVG({ cls, size, style }) {
  const Cmp = ICON_MAP[cls];
  if (!Cmp) return null;
  return <Cmp size={size} style={style} />;
}

const MESES       = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MESES_FULL  = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const PIE_COLORS  = ["#3b82f6","#10b981","#f59e0b","#8b5cf6","#ef4444","#64748b"];

const CATS_DASH = ["Mensualidad", "Setup", "Complemento", "Otros"];
const CAT_CFG_DASH = {
  Mensualidad: { color: "#10b981", light: "#d1fae5", icon: "fas fa-sync-alt"     },
  Setup:       { color: "#3b82f6", light: "#dbeafe", icon: "fas fa-cog"          },
  Complemento: { color: "#f59e0b", light: "#fef3c7", icon: "fas fa-puzzle-piece" },
  Otros:       { color: "#94a3b8", light: "#f1f5f9", icon: "fas fa-tag"          },
};

const fmt = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(v || 0);

const fmtK = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`;
  return fmt(n);
};

// ── Badge de tendencia ────────────────────────────────────────────────────────
function DeltaBadge({ curr, prev, invert = false }) {
  if (!prev || prev === 0) return null;
  const pct    = ((curr - prev) / Math.abs(prev)) * 100;
  const isGood = invert ? pct < 0 : pct > 0;
  const color  = isGood ? "#16a34a" : "#dc2626";
  const bg     = isGood ? "#dcfce7" : "#fee2e2";
  const arrow  = pct > 0 ? "▲" : "▼";
  return (
    <span style={{
      fontSize: "0.63rem", fontWeight: 700, color, background: bg,
      borderRadius: 6, padding: "2px 6px", marginLeft: 4, whiteSpace: "nowrap",
    }}>
      {arrow} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

// ── Tooltip personalizado para Recharts ───────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
      padding: "10px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", fontSize: "0.74rem",
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: "#0f172a" }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{
          display: "flex", justifyContent: "space-between", gap: 20,
          marginBottom: 3, color: p.color,
        }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 700 }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Sección con título ────────────────────────────────────────────────────────
function Section({ icon, title, subtitle, children, style = {} }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 14, padding: "18px 20px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)", height: "100%", ...style,
    }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a", display: "flex", alignItems: "center", gap: 7 }}>
          {icon && <IconSVG cls={icon} size={16} style={{ opacity: 0.7, flexShrink: 0 }} />}
          {title}
        </div>
        {subtitle && <div style={{ fontSize: "0.69rem", color: "#94a3b8", marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
function HomeInicio() {
  const { user } = useContext(AuthContext);

  const mesActual  = new Date().getMonth();      // 0-based
  const anioActual = new Date().getFullYear();
  const hoy        = moment().format("YYYY-MM-DD");
  const inicioAno  = `${anioActual}-01-01`;
  const mesPasado  = mesActual > 0 ? mesActual - 1 : 0;

  const [loading,     setLoading]     = useState(true);
  const [chartData,   setChartData]   = useState([]);
  const [areaData,    setAreaData]    = useState([]);
  const [kpis,        setKpis]        = useState({});
  const [pieData,     setPieData]     = useState([]);
  const [topDeudores, setTopDeudores] = useState([]);
  const [cartera,     setCartera]     = useState(0);
  const [clienteStats,setClienteStats]= useState({ activos: 0, conDeuda: 0 });
  const [altasPorAnio,setAltasPorAnio]= useState([]);
  const [balanceRows, setBalanceRows] = useState([]);
  const [catStats,    setCatStats]    = useState([]);
  const [mensStats,   setMensStats]   = useState(null);

  useEffect(() => {
    const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

    Promise.allSettled([
      axios.get(`${process.env.REACT_APP_URL_GRAFICAS}/balanceMensual/${inicioAno}/${hoy}`, { headers }),
      axios.get(process.env.REACT_APP_URL_CLIENTES, { headers }),
      axios.get(`${process.env.REACT_APP_URL_CARGOS}/fechas/2025-01-01/${hoy}`, { headers }),
      axios.get(process.env.REACT_APP_URL_MENSUALIDADES, { headers }),
    ]).then(([balRes, cliRes, catRes, mensRes]) => {

      // ── Balance mensual ────────────────────────────────────────────────────
      if (balRes.status === "fulfilled") {
        const rows   = balRes.value.data.rows || [];
        const get    = (lbl) => rows.find((r) => r.label === lbl)?.months || Array(12).fill(0);
        const getRow = (lbl) => rows.find((r) => r.label === lbl) || {};

        const venta    = get("Venta");
        const gastos   = get("Gastos");
        const utilidad = get("Utilidad");
        const cobranza = get("Cobranza");
        const retiro   = get("Retiro");

        // Chart compuesto: barras + línea
        const cd = Array.from({ length: mesActual + 1 }, (_, i) => ({
          mes:      MESES[i],
          Venta:    venta[i]    || 0,
          Gastos:   gastos[i]   || 0,
          Cobranza: cobranza[i] || 0,
          Utilidad: utilidad[i] || 0,
        }));
        setChartData(cd);

        // Area chart: utilidad acumulada
        let acum = 0;
        const ad = Array.from({ length: mesActual + 1 }, (_, i) => {
          acum += (utilidad[i] || 0);
          return { mes: MESES[i], Utilidad: utilidad[i] || 0, Acumulada: acum };
        });
        setAreaData(ad);

        // KPIs
        setKpis({
          cargosMes:    venta[mesActual]    || 0,
          cobranzaMes:  cobranza[mesActual] || 0,
          utilidadMes:  utilidad[mesActual] || 0,
          gastosMes:    gastos[mesActual]   || 0,
          retiroMes:    retiro[mesActual]   || 0,
          cargosPrev:   venta[mesPasado]    || 0,
          cobranzaPrev: cobranza[mesPasado] || 0,
          utilidadPrev: utilidad[mesPasado] || 0,
          gastosPrev:   gastos[mesPasado]   || 0,
          retiroPrev:   retiro[mesPasado]   || 0,
          ventaTotal:   getRow("Venta").total    || 0,
          gastosTotal:  getRow("Gastos").total   || 0,
          utilidadTotal:getRow("Utilidad").total || 0,
          cobranzaTotal:getRow("Cobranza").total || 0,
          retiroTotal:  getRow("Retiro").total   || 0,
        });

        // Mini balance últimos 6 meses
        const ini6 = Math.max(0, mesActual - 5);
        setBalanceRows(
          Array.from({ length: mesActual - ini6 + 1 }, (_, i) => i + ini6).map((i) => ({
            mes:      MESES_FULL[i],
            venta:    venta[i]    || 0,
            gastos:   gastos[i]   || 0,
            utilidad: utilidad[i] || 0,
            cobranza: cobranza[i] || 0,
            retiro:   retiro[i]   || 0,
          }))
        );
      }

      // ── Clientes ───────────────────────────────────────────────────────────
      if (cliRes.status === "fulfilled") {
        const all      = cliRes.value.data || [];
        const activos  = all.filter((c) => c.is_active === "Si");
        const conSaldo = activos
          .filter((c) => (c.saldo || 0) > 0)
          .sort((a, b) => (b.saldo || 0) - (a.saldo || 0));

        const totalCartera = conSaldo.reduce((s, c) => s + (c.saldo || 0), 0);
        setCartera(totalCartera);
        setClienteStats({ activos: activos.length, conDeuda: conSaldo.length });
        setTopDeudores(conSaldo.slice(0, 6));

        // Clientes activos por año de alta
        const porAnio = activos.reduce((acc, c) => {
          const a = (c.anio || "").toString().trim();
          if (!a) return acc;
          acc[a] = (acc[a] || 0) + 1;
          return acc;
        }, {});
        setAltasPorAnio(
          Object.keys(porAnio)
            .sort()
            .map((a) => ({ anio: a, clientes: porAnio[a] }))
        );

        // Donut top 5 + Otros
        const otros = conSaldo.slice(5).reduce((s, c) => s + (c.saldo || 0), 0);
        setPieData([
          ...conSaldo.slice(0, 5).map((c) => ({
            name:  c.nombre_comercial.length > 18 ? c.nombre_comercial.slice(0, 18) + "…" : c.nombre_comercial,
            value: c.saldo || 0,
          })),
          ...(otros > 0 ? [{ name: "Otros", value: otros }] : []),
        ]);
      }

      // ── Cargos por categoría (desde 2025-01-01) ───────────────────────────
      if (catRes.status === "fulfilled") {
        const cargos = catRes.value.data || [];
        const totalImp = cargos.reduce((s, c) => s + (c.importe || 0), 0);
        const stats = CATS_DASH.map((cat) => {
          const rows = cargos.filter((c) => (c.categoria || "Otros") === cat);
          const imp  = rows.reduce((s, c) => s + (c.importe || 0), 0);
          const abo  = rows.reduce((s, c) => s + (c.abonos  || 0), 0);
          const sal  = rows.reduce((s, c) => s + (c.saldo   || 0), 0);
          return {
            name:    cat,
            count:   rows.length,
            importe: imp,
            abonos:  abo,
            saldo:   sal,
            pctImp:  totalImp > 0 ? ((imp / totalImp) * 100).toFixed(1) : "0.0",
            cobPct:  imp > 0 ? parseFloat(((abo / imp) * 100).toFixed(1)) : 0,
            totalImp,
          };
        });
        setCatStats(stats);
      }

      // ── Mensualidades ─────────────────────────────────────────────────────
      if (mensRes.status === "fulfilled") {
        const all  = mensRes.value.data || [];
        const base = all.filter((a) => a.clientes?.[0]?.empresa !== "Sistemify");
        const fmtM = (v) => "$" + new Intl.NumberFormat("en-US").format(v);
        const sumM = (arr) => arr.reduce((s, a) => s + (Number(a.importe_actual) || 0), 0);

        const mensualActivas = base.filter((a) => a.frecuencia === "Mensual" && a.is_active === "Si");
        const anualActivas   = base.filter((a) => a.frecuencia === "Anual"   && a.is_active === "Si");
        const inactivas      = base.filter((a) => a.is_active !== "Si");
        const totalActivas   = base.filter((a) => a.is_active === "Si");

        setMensStats({
          mensualActivas: { count: mensualActivas.length, importe: fmtM(sumM(mensualActivas)) },
          anualActivas:   { count: anualActivas.length,   importe: fmtM(sumM(anualActivas))   },
          inactivas:      { count: inactivas.length,      importe: fmtM(sumM(inactivas))      },
          totalActivas:   { count: totalActivas.length,   importe: fmtM(sumM(totalActivas))   },
        });
      }

      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── KPI Cards config ────────────────────────────────────────────────────────
  const kpiCards = [
    {
      label: "Cargos del mes",    value: fmt(kpis.cargosMes),
      curr: kpis.cargosMes,      prev: kpis.cargosPrev,
      icon: "fas fa-file-invoice-dollar", color: "#3b82f6", bg: "#eff6ff",
      sub: `Acum ${MESES_FULL[mesActual]}: ${fmtK(kpis.ventaTotal)}`,
    },
    {
      label: "Cobranza del mes",  value: fmt(kpis.cobranzaMes),
      curr: kpis.cobranzaMes,    prev: kpis.cobranzaPrev,
      icon: "fas fa-hand-holding-usd", color: "#10b981", bg: "#f0fdf4",
      sub: `Acum: ${fmtK(kpis.cobranzaTotal)}`,
    },
    {
      label: "Retiro del mes",    value: fmt(kpis.retiroMes),
      curr: kpis.retiroMes,      prev: kpis.retiroPrev,
      icon: "fas fa-chart-line",  color: "#f59e0b", bg: "#fffbeb",
      sub: `Acum: ${fmtK(kpis.retiroTotal)}`,
    },
    {
      label: "Gastos del mes",    value: fmt(kpis.gastosMes),
      curr: kpis.gastosMes,      prev: kpis.gastosPrev,
      icon: "fas fa-receipt",     color: "#ef4444", bg: "#fff1f2",
      invert: true,
      sub: `Acum: ${fmtK(kpis.gastosTotal)}`,
    },
    {
      label: "Clientes activos",
      value: loading ? "…" : clienteStats.activos,
      icon: "fas fa-users",       color: "#8b5cf6", bg: "#f5f3ff",
      sub: loading ? "" : `${clienteStats.conDeuda} con saldo pendiente`,
    },
    {
      label: "Cartera total",     value: fmt(cartera),
      icon: "fas fa-wallet",      color: "#0ea5e9", bg: "#f0f9ff",
      sub: "Saldo pendiente de clientes",
    },
  ];

  // Si el usuario NO tiene permiso de Menú Usuarios, no ve el dashboard del inicio.
  // Se le manda a Lugares Fiestas (su módulo) en su lugar.
  if (user && user.menu_usuarios === false) {
    return <Redirect to="/LugaresFiestas" />;
  }

  return (
    <>
      <Header />
      <div style={{ padding: "4px 20px 48px", maxWidth: 1300, margin: "0 auto" }}>

        {/* ── Greeting ─────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h2 style={{ fontWeight: 700, fontSize: "1.2rem", color: "#0f172a", margin: 0 }}>
              Bienvenido{user?.nombre ? `, ${user.nombre}` : ""} 👋
            </h2>
            <p style={{ color: "#64748b", fontSize: "0.78rem", margin: 0 }}>
              {moment().format("dddd, D [de] MMMM [de] YYYY")}
            </p>
          </div>
          <div style={{
            background: "linear-gradient(135deg,#0f172a,#1e40af)",
            color: "#fff", borderRadius: 10, padding: "8px 16px",
            fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.02em",
          }}>
            <IcCalendar size={15} style={{ marginRight: 6, display: "inline-block" }} />
            {MESES_FULL[mesActual]} {anioActual}
          </div>
        </div>

        {/* ── KPI Cards ────────────────────────────────────────────────────── */}
        <div className="row mb-3">
          {kpiCards.map((k, i) => (
            <div key={i} className="col-6 col-sm-4 col-xl-2 mb-3">
              <div style={{
                background: "#fff", borderRadius: 14, padding: "16px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)", height: "100%",
                borderTop: `3px solid ${k.color}`, transition: "box-shadow 0.2s, transform 0.2s",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.10)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.07)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                {/* Icon + delta */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, background: k.bg,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem",
                  }}>
                    <IconSVG cls={k.icon} size={18} style={{ color: k.color }} />
                  </div>
                  {k.prev != null && <DeltaBadge curr={k.curr} prev={k.prev} invert={k.invert} />}
                </div>
                {/* Value */}
                <div style={{ fontSize: "1.05rem", fontWeight: 800, color: k.color, lineHeight: 1.1, marginBottom: 3 }}>
                  {loading && i < 4 ? <span style={{ color: "#cbd5e1" }}>…</span> : k.value}
                </div>
                <div style={{ fontSize: "0.66rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>
                  {k.label}
                </div>
                {k.sub && <div style={{ fontSize: "0.63rem", color: "#94a3b8" }}>{k.sub}</div>}
              </div>
            </div>
          ))}
        </div>

        {/* ── Gráficas principales ─────────────────────────────────────────── */}
        <div className="row mb-3">

          {/* ComposedChart: Venta / Gastos / Cobranza / Utilidad */}
          <div className="col-lg-8 mb-3">
            <Section
              icon="fas fa-chart-bar"
              title={`Rendimiento mensual ${anioActual}`}
              subtitle="Venta · Gastos · Cobranza · Utilidad (línea)"
            >
              <ResponsiveContainer width="100%" height={255}>
                <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradVenta" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.6} />
                    </linearGradient>
                    <linearGradient id="gradGastos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fca5a5" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#fca5a5" stopOpacity={0.6} />
                    </linearGradient>
                    <linearGradient id="gradCobranza" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6ee7b7" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#6ee7b7" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtK} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={50} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "0.71rem", paddingTop: 8 }} />
                  <Bar dataKey="Venta"    fill="url(#gradVenta)"    radius={[4,4,0,0]} maxBarSize={24} />
                  <Bar dataKey="Gastos"   fill="url(#gradGastos)"   radius={[4,4,0,0]} maxBarSize={24} />
                  <Bar dataKey="Cobranza" fill="url(#gradCobranza)" radius={[4,4,0,0]} maxBarSize={24} />
                  <Line
                    dataKey="Utilidad" type="monotone"
                    stroke="#f59e0b" strokeWidth={2.5}
                    dot={{ r: 4, fill: "#f59e0b", strokeWidth: 2, stroke: "#fff" }}
                    activeDot={{ r: 6 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Section>
          </div>

          {/* Donut: distribución de cartera */}
          <div className="col-lg-4 mb-3">
            <Section
              icon="fas fa-wallet"
              title="Cartera pendiente"
              subtitle="Distribución por cliente"
              style={{ display: "flex", flexDirection: "column" }}
            >
              {pieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie
                        data={pieData} cx="50%" cy="50%"
                        innerRadius={48} outerRadius={72}
                        paddingAngle={3} dataKey="value"
                        startAngle={90} endAngle={-270}
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Leyenda manual */}
                  <div style={{ flex: 1 }}>
                    {pieData.map((d, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        fontSize: "0.69rem", marginBottom: 4,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#334155" }}>
                          <span style={{
                            width: 8, height: 8, borderRadius: "50%",
                            background: PIE_COLORS[i % PIE_COLORS.length],
                            display: "inline-block", flexShrink: 0,
                          }} />
                          <span style={{ maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {d.name}
                          </span>
                        </div>
                        <span style={{ fontWeight: 700, color: PIE_COLORS[i % PIE_COLORS.length] }}>
                          {fmtK(d.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: "0.80rem" }}>
                  🎉 Sin saldos pendientes
                </div>
              )}
            </Section>
          </div>
        </div>

        {/* ── Segunda fila de gráficas ──────────────────────────────────────── */}
        <div className="row mb-3">

          {/* Área: utilidad mensual + acumulada */}
          <div className="col-lg-6 mb-3">
            <Section
              icon="fas fa-chart-area"
              title="Utilidad — mensual y acumulada"
              subtitle={`Progreso del año ${anioActual}`}
            >
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={areaData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradAcum" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gradUt" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.30} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtK} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={50} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "0.71rem", paddingTop: 6 }} />
                  <Area dataKey="Acumulada" type="monotone" stroke="#3b82f6" strokeWidth={2} fill="url(#gradAcum)" dot={false} />
                  <Area dataKey="Utilidad"  type="monotone" stroke="#f59e0b" strokeWidth={2} fill="url(#gradUt)"   dot={{ r: 3, fill: "#f59e0b", stroke: "#fff", strokeWidth: 1.5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Section>
          </div>

          {/* Top deudores con barras de progreso */}
          <div className="col-lg-6 mb-3">
            <Section
              icon="fas fa-exclamation-circle"
              title="Top deudores"
              subtitle="Clientes con mayor saldo pendiente"
            >
              {topDeudores.length > 0 ? topDeudores.map((c, i) => {
                const pct = cartera > 0 ? ((c.saldo || 0) / cartera) * 100 : 0;
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                      <span style={{ fontSize: "0.73rem", fontWeight: 600, color: "#334155", maxWidth: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: 18, height: 18, borderRadius: "50%", fontSize: "0.6rem",
                          background: PIE_COLORS[i % PIE_COLORS.length], color: "#fff",
                          fontWeight: 700, marginRight: 6, flexShrink: 0,
                        }}>
                          {i + 1}
                        </span>
                        {c.nombre_comercial}
                      </span>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: "0.75rem", fontWeight: 700, color: PIE_COLORS[i % PIE_COLORS.length] }}>
                          {fmt(c.saldo)}
                        </span>
                        <span style={{ fontSize: "0.62rem", color: "#94a3b8", marginLeft: 6 }}>
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div style={{ background: "#f1f5f9", borderRadius: 6, height: 7, overflow: "hidden" }}>
                      <div style={{
                        width: `${pct}%`, height: "100%",
                        background: `linear-gradient(90deg, ${PIE_COLORS[i % PIE_COLORS.length]}, ${PIE_COLORS[i % PIE_COLORS.length]}aa)`,
                        borderRadius: 6, transition: "width 0.8s ease",
                      }} />
                    </div>
                  </div>
                );
              }) : (
                <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: "0.80rem" }}>
                  🎉 Sin deudores pendientes
                </div>
              )}
            </Section>
          </div>
        </div>

        {/* ── Mini Balance ─────────────────────────────────────────────────── */}
        <div className="row mb-3">
          <div className="col-12">
            <Section
              icon="fas fa-table"
              title="Balance mensual — últimos meses"
              subtitle={`Detalle mes a mes del año ${anioActual}`}
            >
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.76rem" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                      {["Mes", "Venta", "Gastos", "Gastos %", "Utilidad", "Utilidad %", "Cobranza", "Retiro"].map((h) => (
                        <th key={h} style={{
                          padding: "6px 10px",
                          textAlign: h === "Mes" ? "left" : "right",
                          color: "#64748b", fontWeight: 700,
                          fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.05em",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {balanceRows.map((r, i) => {
                      const gastosPct   = r.venta > 0 ? (r.gastos   / r.venta) * 100 : 0;
                      const utilidadPct = r.venta > 0 ? (r.utilidad / r.venta) * 100 : 0;
                      const isCurrentMonth = (i === balanceRows.length - 1);
                      return (
                        <tr key={i} style={{
                          borderBottom: "1px solid #f8fafc",
                          background: isCurrentMonth ? "#f0f9ff" : (i % 2 === 0 ? "#fff" : "#fafafa"),
                        }}>
                          <td style={{ padding: "7px 10px", fontWeight: isCurrentMonth ? 800 : 600, color: isCurrentMonth ? "#0ea5e9" : "#334155" }}>
                            {isCurrentMonth && <span style={{ fontSize: "0.6rem", background: "#0ea5e9", color: "#fff", borderRadius: 4, padding: "1px 5px", marginRight: 6, fontWeight: 700 }}>HOY</span>}
                            {r.mes.slice(0, 3)}
                          </td>
                          <td style={{ padding: "7px 10px", textAlign: "right", color: "#3b82f6", fontWeight: 700 }}>{fmtK(r.venta)}</td>
                          <td style={{ padding: "7px 10px", textAlign: "right", color: "#ef4444" }}>{fmtK(r.gastos)}</td>
                          <td style={{ padding: "7px 10px", textAlign: "right" }}>
                            <span style={{
                              fontSize: "0.68rem", fontWeight: 600, borderRadius: 5, padding: "2px 6px",
                              background: gastosPct > 60 ? "#fee2e2" : gastosPct > 40 ? "#fff7ed" : "#dcfce7",
                              color:      gastosPct > 60 ? "#dc2626" : gastosPct > 40 ? "#d97706" : "#16a34a",
                            }}>
                              {gastosPct.toFixed(1)}%
                            </span>
                          </td>
                          <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: r.utilidad >= 0 ? "#10b981" : "#ef4444" }}>
                            {fmtK(r.utilidad)}
                          </td>
                          <td style={{ padding: "7px 10px", textAlign: "right" }}>
                            <span style={{
                              fontSize: "0.68rem", fontWeight: 600, borderRadius: 5, padding: "2px 6px",
                              background: utilidadPct >= 30 ? "#dcfce7" : utilidadPct >= 15 ? "#fff7ed" : "#fee2e2",
                              color:      utilidadPct >= 30 ? "#16a34a" : utilidadPct >= 15 ? "#d97706" : "#dc2626",
                            }}>
                              {utilidadPct.toFixed(1)}%
                            </span>
                          </td>
                          <td style={{ padding: "7px 10px", textAlign: "right", color: "#0ea5e9" }}>{fmtK(r.cobranza)}</td>
                          <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: "#10b981" }}>{fmtK(r.retiro)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 10, textAlign: "right" }}>
                <a href="/BalanceMensual" style={{ fontSize: "0.73rem", color: "#3b82f6", textDecoration: "none", fontWeight: 700 }}>
                  Ver balance completo →
                </a>
              </div>
            </Section>
          </div>
        </div>

        {/* ── Cargos por Categoría ─────────────────────────────────────────── */}
        {catStats.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <Section
                icon="fas fa-chart-pie"
                title="Cargos por Categoría"
                subtitle="Desde 01 enero 2025 · cobranza acumulada por tipo"
              >
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                        {[
                          { h: "Categoría",   align: "left"   },
                          { h: "Cargos",      align: "right"  },
                          { h: "Importe",     align: "right"  },
                          { h: "Cobrado",     align: "right"  },
                          { h: "Pendiente",   align: "right"  },
                          { h: "Cobranza",    align: "center" },
                          { h: "% del Total", align: "left"   },
                        ].map(({ h, align }) => (
                          <th key={h} style={{
                            padding: "7px 12px",
                            textAlign: align,
                            color: "#64748b",
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {catStats.map((s, i) => {
                        const cfg = CAT_CFG_DASH[s.name];
                        return (
                          <tr key={s.name}
                            style={{ borderBottom: i < catStats.length - 1 ? "1px solid #f8fafc" : "none" }}
                            onMouseEnter={(e) => e.currentTarget.style.background = cfg.light}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                          >
                            {/* Categoría */}
                            <td style={{ padding: "11px 12px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                <div style={{
                                  width: 28, height: 28, borderRadius: 7,
                                  background: cfg.light,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                  <IconSVG cls={cfg.icon} size={14} style={{ color: cfg.color }} />
                                </div>
                                <span style={{ fontWeight: 700, color: "#0f172a" }}>{s.name}</span>
                              </div>
                            </td>
                            {/* Cargos */}
                            <td style={{ padding: "11px 12px", textAlign: "right", color: "#475569", fontWeight: 600 }}>
                              {s.count}
                            </td>
                            {/* Importe */}
                            <td style={{ padding: "11px 12px", textAlign: "right", fontWeight: 700, color: "#0f172a" }}>
                              {fmt(s.importe)}
                            </td>
                            {/* Cobrado */}
                            <td style={{ padding: "11px 12px", textAlign: "right", fontWeight: 600, color: "#10b981" }}>
                              {fmt(s.abonos)}
                            </td>
                            {/* Pendiente */}
                            <td style={{ padding: "11px 12px", textAlign: "right",
                              fontWeight: s.saldo > 0 ? 700 : 400,
                              color: s.saldo > 0 ? "#f59e0b" : "#cbd5e1" }}>
                              {fmt(s.saldo)}
                            </td>
                            {/* Cobranza badge */}
                            <td style={{ padding: "11px 12px", textAlign: "center" }}>
                              <span style={{
                                background: s.cobPct >= 80 ? "#dcfce7" : s.cobPct >= 50 ? "#fef3c7" : "#fee2e2",
                                color: s.cobPct >= 80 ? "#065f46" : s.cobPct >= 50 ? "#78350f" : "#991b1b",
                                borderRadius: 20, padding: "3px 10px",
                                fontSize: "0.72rem", fontWeight: 700,
                              }}>
                                {s.cobPct.toFixed(1)}%
                              </span>
                            </td>
                            {/* Progress bar % del total */}
                            <td style={{ padding: "11px 12px", minWidth: 140 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{
                                  flexGrow: 1, height: 7,
                                  background: "#f1f5f9", borderRadius: 10, overflow: "hidden",
                                }}>
                                  <div style={{
                                    height: "100%",
                                    width: `${Math.min(parseFloat(s.pctImp), 100)}%`,
                                    background: `linear-gradient(90deg, ${cfg.color}, ${cfg.color}bb)`,
                                    borderRadius: 10,
                                    transition: "width 0.9s ease",
                                  }} />
                                </div>
                                <span style={{
                                  fontSize: "0.72rem", fontWeight: 800,
                                  color: cfg.color, minWidth: 36, textAlign: "right",
                                }}>
                                  {s.pctImp}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: "2px solid #e2e8f0", background: "#f8fafc" }}>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{ fontWeight: 700, color: "#475569", fontSize: "0.68rem",
                            textTransform: "uppercase", letterSpacing: "0.07em" }}>TOTAL</span>
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#0f172a" }}>
                          {catStats.reduce((s, r) => s + r.count, 0)}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, color: "#0f172a" }}>
                          {fmt(catStats.reduce((s, r) => s + r.importe, 0))}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#10b981" }}>
                          {fmt(catStats.reduce((s, r) => s + r.abonos, 0))}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#f59e0b" }}>
                          {fmt(catStats.reduce((s, r) => s + r.saldo, 0))}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>
                          {(() => {
                            const totalImp = catStats.reduce((s, r) => s + r.importe, 0);
                            const totalAbo = catStats.reduce((s, r) => s + r.abonos,  0);
                            const pct = totalImp > 0 ? (totalAbo / totalImp) * 100 : 0;
                            return (
                              <span style={{
                                background: pct >= 80 ? "#dcfce7" : pct >= 50 ? "#fef3c7" : "#fee2e2",
                                color: pct >= 80 ? "#065f46" : pct >= 50 ? "#78350f" : "#991b1b",
                                borderRadius: 20, padding: "3px 10px",
                                fontSize: "0.72rem", fontWeight: 700,
                              }}>
                                {pct.toFixed(1)}%
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600 }}>100%</span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div style={{ marginTop: 10, textAlign: "right" }}>
                  <a href="/ReporteCargos" style={{ fontSize: "0.73rem", color: "#3b82f6", textDecoration: "none", fontWeight: 700 }}>
                    Ver reporte completo →
                  </a>
                </div>
              </Section>
            </div>
          </div>
        )}

        {/* ── Mensualidades resumen ────────────────────────────────────────── */}
        {mensStats && (
          <div className="row mb-3">
            <div className="col-12">
              <Section
                icon="fas fa-calendar-alt"
                title="Mensualidades"
                subtitle="Cobros recurrentes activos e inactivos · Excepto Sistemify"
              >
                <div className="row">
                  {[
                    {
                      label: "Mensual Activas",
                      count: mensStats.mensualActivas.count,
                      importe: mensStats.mensualActivas.importe,
                      icon: "fas fa-sync-alt",
                      color: "#8b5cf6", bg: "#f5f3ff",
                      desc: "Cobro mensual recurrente",
                    },
                    {
                      label: "Anual Activas",
                      count: mensStats.anualActivas.count,
                      importe: mensStats.anualActivas.importe,
                      icon: "fas fa-calendar-check",
                      color: "#3b82f6", bg: "#eff6ff",
                      desc: "Cobro anual recurrente",
                    },
                    {
                      label: "Total Activas",
                      count: mensStats.totalActivas.count,
                      importe: mensStats.totalActivas.importe,
                      icon: "fas fa-check-circle",
                      color: "#10b981", bg: "#f0fdf4",
                      desc: "Mensual + Anual activas",
                    },
                    {
                      label: "Inactivas",
                      count: mensStats.inactivas.count,
                      importe: mensStats.inactivas.importe,
                      icon: "fas fa-pause-circle",
                      color: "#ef4444", bg: "#fff1f2",
                      desc: "Sin cobro activo",
                    },
                  ].map((card, i) => (
                    <div key={i} className="col-6 col-md-3 mb-2">
                      <div style={{
                        background: card.bg,
                        borderRadius: 12,
                        padding: "16px 18px",
                        border: `1px solid ${card.color}22`,
                        borderTop: `3px solid ${card.color}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        height: "100%",
                      }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: 11,
                          background: "#fff", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: `0 2px 8px ${card.color}22`,
                        }}>
                          <IconSVG cls={card.icon} size={22} style={{ color: card.color }} />
                        </div>
                        <div>
                          <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                            {card.count}
                          </div>
                          <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#64748b",
                            textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>
                            {card.label}
                          </div>
                          <div style={{ fontSize: "0.72rem", color: card.color, fontWeight: 700, marginTop: 2 }}>
                            {card.importe}
                          </div>
                          <div style={{ fontSize: "0.63rem", color: "#94a3b8", marginTop: 1 }}>
                            {card.desc}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, textAlign: "right" }}>
                  <a href="/ListadoMensualidades" style={{ fontSize: "0.73rem", color: "#8b5cf6", textDecoration: "none", fontWeight: 700 }}>
                    Ver mensualidades →
                  </a>
                </div>
              </Section>
            </div>
          </div>
        )}

        {/* ── Clientes activos por año de alta ─────────────────────────────── */}
        <div className="row mb-3">
          <div className="col-12">
            <Section
              icon="fas fa-users"
              title="Clientes activos por año de alta"
              subtitle={`${clienteStats.activos} clientes activos · agrupados por año de registro`}
            >
              {altasPorAnio.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <ComposedChart data={altasPorAnio} margin={{ top: 24, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradAltas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="#8b5cf6" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0.75} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="anio" tick={{ fontSize: 12, fill: "#64748b", fontWeight: 600 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={36} />
                    <Tooltip
                      cursor={{ fill: "rgba(139,92,246,0.06)" }}
                      formatter={(v) => [`${v} cliente${v === 1 ? "" : "s"}`, "Activos"]}
                      labelFormatter={(l) => `Año ${l}`}
                      contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: "0.75rem" }}
                    />
                    <Bar
                      dataKey="clientes"
                      fill="url(#gradAltas)"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={64}
                      label={{ position: "top", fontSize: 13, fill: "#334155", fontWeight: 700 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: "0.80rem" }}>
                  Sin datos de año de alta en los clientes
                </div>
              )}
            </Section>
          </div>
        </div>

        {/* ── Accesos rápidos ───────────────────────────────────────────────── */}
        <div className="row mb-2">
          {[
            { label: "Nuevo cargo",      icon: "fas fa-plus-circle",       href: "/CargosCreate",   color: "#3b82f6" },
            { label: "Registrar pago",   icon: "fas fa-hand-holding-usd",  href: "/AbonosCreate",   color: "#10b981" },
            { label: "Clientes",         icon: "fas fa-users",              href: "/ListadoClientes",color: "#8b5cf6" },
            { label: "Balance mensual",  icon: "fas fa-chart-bar",          href: "/BalanceMensual", color: "#f59e0b" },
            { label: "Balance anual",    icon: "fas fa-calendar-alt",       href: "/BalanceAnual",   color: "#0ea5e9" },
            { label: "Estado de cuenta", icon: "fas fa-file-alt",           href: "/EstadoCuenta",   color: "#ef4444" },
          ].map((link, i) => (
            <div key={i} className="col-4 col-md-2 mb-2">
              <a
                href={link.href}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", gap: 8, padding: "16px 8px",
                  background: "#fff", borderRadius: 12,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
                  textDecoration: "none", color: "#334155",
                  transition: "box-shadow 0.18s, transform 0.18s",
                  fontSize: "0.72rem", fontWeight: 600, textAlign: "center",
                  borderBottom: `3px solid ${link.color}`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.11)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.07)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <IconSVG cls={link.icon} size={26} style={{ color: link.color }} />
                {link.label}
              </a>
            </div>
          ))}
        </div>

        <p style={{ color: "#cbd5e1", fontSize: "0.68rem", textAlign: "center", marginTop: 16 }}>
          Lynqro Admin · Datos en tiempo real · {moment().format("D [de] MMMM YYYY, HH:mm")}
        </p>
      </div>
    </>
  );
}

export default HomeInicio;
